import React, { useRef, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, KeyboardAvoidingView, Platform, Keyboard, TouchableWithoutFeedback } from 'react-native';
import { Text } from '@/components/common/text';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { ChatWindow } from '@/components/admin/messages/ChatWindow';
import { ChatOptionsMenu } from '@/components/admin/messages/ChatOptionsMenu';
import { ThreadOwnerModal } from '@/components/admin/messages/ThreadOwnerModal';
import { ChatDetailsModal } from '@/components/admin/messages/ChatDetailsModal';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useConversation } from '@/hooks/admin/useMessages';
import type { IConversationAccount } from '@/types/admin/messages';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useWebKeyboardViewport } from '@/hooks/common/useWebKeyboardViewport';

type ChatScreenProps = StackScreenProps<AdminStackParamList, 'Chat'>;

export const ChatScreen: React.FC<ChatScreenProps> = ({
  navigation,
  route,
}) => {
  const { redesign } = useAdminColors();
  const { conversationId } = route.params;
  const { conversation } = useConversation(conversationId);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showThreadOwnerModal, setShowThreadOwnerModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  // The participant to open once the details sheet has finished closing.
  //
  // The sheet is a native iOS modal and this stack navigates without
  // animation, so pushing ResidentDetails in the same tick as closing it
  // detaches this screen while the sheet is still animating out. The native
  // dismissal then never reports completion, RN's Modal keeps its (transparent,
  // screen-sized) host view mounted above everything here, and every touch on
  // the way back lands on that host: the screen looks fine but nothing
  // responds. Navigating from the sheet's onDismiss avoids the race. On web
  // and Android the host unmounts as soon as `visible` is false, so those
  // navigate right away.
  const pendingParticipantRef = useRef<{ accountId: number; leaseId: number } | null>(null);
  const { height: viewportHeight } = useWebKeyboardViewport();

  const openResidentDetails = (target: { accountId: number; leaseId: number }) => {
    navigation.navigate('ResidentDetails', { ...target, mode: 'account' });
  };

  const handleParticipantPress = (participant: IConversationAccount) => {
    const accountId = participant?.assigneeAccount?.id;
    const leaseId = participant?.assigneeAccount?.leaseCustomers?.[0]?.lease?.id;
    if (!accountId || !leaseId) return;
    setShowDetailsModal(false);
    if (Platform.OS === 'ios') {
      pendingParticipantRef.current = { accountId, leaseId };
    } else {
      openResidentDetails({ accountId, leaseId });
    }
  };

  const handleDetailsDismissed = () => {
    const target = pendingParticipantRef.current;
    if (!target) return;
    pendingParticipantRef.current = null;
    openResidentDetails(target);
  };
  // On web the stack navigator's card grows with its content and lets the
  // document scroll (it switches to min-height:100% whenever the navigator
  // fills the body, which a phone browser always does), so a flex:1 chat grew
  // with every message: the list never scrolled, the composer sank below the
  // fold and the header scrolled away. Pin the screen to the viewport instead.
  // The height is keyboard-aware: it moves to the keyboard-open height the
  // moment the composer is focused (iOS Safari only reports the keyboard once
  // it has finished sliding, then pans to reveal a field the layout has not
  // made room for), and back the moment it blurs.
  const rootSizing = Platform.OS === 'web' ? { height: viewportHeight } : styles.fullContainer;

  // Render chat header
  const renderChatHeader = () => {
    if (!conversation) return null;

    const conversationAccounts = (conversation.accounts || []).filter(
      (acct) => acct.ConversationAccount?.type === 'resident'
    );

    const recipientName = conversationAccounts.length === 1
      ? conversationAccounts[0].firstName && conversationAccounts[0].lastName
        ? `${conversationAccounts[0].firstName} ${conversationAccounts[0].lastName}`
        : conversationAccounts[0].email
      : `${conversationAccounts.length} Recipients`;

    return (
      <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
        <View
          style={[
            styles.chatHeader,
            {
              backgroundColor: redesign.bg.base,
              borderBottomColor: redesign.card.border,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <SVG_COMPONENTS.CHEVRON_LEFT
              width={24}
              height={24}
              color={redesign.text.primary}
            />
          </TouchableOpacity>

          <View style={styles.chatHeaderContent}>
            <Text
              style={[styles.chatHeaderTitle, { color: redesign.text.primary }]}
              numberOfLines={1}
            >
              {conversation.subject}
            </Text>
            <Text
              style={[styles.chatHeaderSubtitle, { color: redesign.text.muted }]}
              numberOfLines={1}
            >
              {recipientName}
            </Text>
          </View>

          <TouchableOpacity
            style={styles.chatMenuButton}
            onPress={() => setShowOptionsMenu(true)}
          >
            <SVG_COMPONENTS.EDIT_DOT
              width={24}
              height={24}
              color={redesign.text.primary}
            />
          </TouchableOpacity>
        </View>
      </TouchableWithoutFeedback>
    );
  };

  return (
    <AdminErrorBoundary context="ChatScreen">
      <SafeAreaView style={[rootSizing, { backgroundColor: redesign.bg.base }]}>
        <TouchableWithoutFeedback onPress={() => setShowOptionsMenu(false)}>
          <KeyboardAvoidingView
            style={styles.keyboardContainer}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
            enabled={true}
          >
            {renderChatHeader()}
            <View style={[styles.container, { backgroundColor: redesign.bg.base }]}>
              <ChatWindow conversationId={conversationId} />
            </View>

            {/* Chat Options Menu */}
            {conversation && (
              <ChatOptionsMenu
                visible={showOptionsMenu}
                onClose={() => setShowOptionsMenu(false)}
                conversation={conversation}
                onEditPress={() => navigation.navigate('EditMessage', { conversationId })}
                onThreadOwnerPress={() => setShowThreadOwnerModal(true)}
                onShowDetailsPress={() => setShowDetailsModal(true)}
                position={{ top: 64, right: 16 }}
              />
            )}

            {/* Thread Owner Modal */}
            {conversation && (
              <ThreadOwnerModal
                visible={showThreadOwnerModal}
                onClose={() => setShowThreadOwnerModal(false)}
                conversation={conversation}
              />
            )}

            {/* Chat Details Modal */}
            {conversation && (
              <ChatDetailsModal
                visible={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                onDismiss={handleDetailsDismissed}
                conversation={conversation}
                onParticipantPress={handleParticipantPress}
              />
            )}
          </KeyboardAvoidingView>
        </TouchableWithoutFeedback>
      </SafeAreaView>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  fullContainer: {
    flex: 1,
  },
  keyboardContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  chatHeaderContent: {
    flex: 1,
    marginRight: 8,
  },
  chatHeaderTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 2,
  },
  chatHeaderSubtitle: {
    fontSize: 14,
  },
  chatMenuButton: {
    padding: 8,
  },
});
